<template>
  <div
    class="kanban-card-wrapper"
    :class="[
      isActiveDropTarget ? activeDropPosition : null,
      {
        visible:
          !isDraggingSelf && isActiveDropTarget && activeDropPosition !== null,
      },
    ]"
    @dragenter.prevent="onDragOver"
    @dragover.prevent="onDragOver"
    draggable="true"
    @dragstart="onDragStart"
    @drop.stop.prevent="onDrop"
    @dragend="onDragEnd"
  >
    <KanbanAvatarGroup
      v-if="false"
      :popoverID="`kanban-task-${task.id}`"
      :currentItem="task"
    />

    <div class="kanban-card" :class="{ selected: isItemSelected(task.id) }">
      <div
        v-if="!dragContext.isDragging"
        class="kanban-card-overlay"
        :class="{
          'is-loading': task?.loading,
          checked: isItemSelected(task.id),
        }"
      >
        <div class="top-content" v-show="!task?.loading">
          <div class="checkbox-container">
            <span :class="{ checked: isItemSelected(task.id) }">
              <input
                type="checkbox"
                :checked="isItemSelected(task.id)"
                @click="
                  $emit('onActionTrigger', {
                    action: 'oncheckboxclick',
                    e: $event,
                    item: task,
                  })
                "
              />
            </span>
          </div>

          <ul class="tools">
            <template v-for="action in actions">
              <li
                v-if="action.visible && action.name !== 'more'"
                :key="action.name"
                :class="action.name"
                :title="action.label"
                @click="
                  $emit('onActionTrigger', {
                    action: action.name,
                    item: task,
                  })
                "
              ></li>

              <generalDropdown
                :isCaretVisible="false"
                v-if="action.visible && action.name == 'more'"
              >
                <template v-slot:iconContainer>
                  <li
                    :key="action.name"
                    :title="action.label"
                    :class="action.name"
                    class="dropdown-toggle"
                  ></li>
                </template>

                <template v-slot:optionData>
                  <b-dropdown-item
                    draggable="false"
                    v-for="flow in actionFlowList"
                    :key="flow.id"
                    class="dropdown-action-item"
                    @click.native.capture.stop.prevent="
                      $emit('onActionTrigger', {
                        action: 'runactionflow',
                        item: task,
                        flow: flow,
                      })
                    "
                    :class="{
                      'pointer-disabled': actionDropdownApiLoading,
                    }"
                    v-b-tooltip.top.hover.offset="{ offset: 100 }"
                    :title="flow.name"
                  >
                    <img
                      class="workflow-icon"
                      src="../../assets/img/workflow-icon-new-light.svg"
                    />
                    <span class="workflow-action">
                      {{ flow.name }}
                    </span>

                    <CustomLoader
                      v-if="isDatabaseDropDownLoading(flow.id, task.id)"
                      overlay
                      absolute
                      :size="25"
                    />
                  </b-dropdown-item>

                  <b-dropdown-item
                    v-if="userHasPermissionToAddFlow"
                    class="dropdown-action-item"
                    @click="
                      $emit('onActionTrigger', {
                        action: 'createnewactionflow',
                        item: task,
                      })
                    "
                    :class="{
                      'pointer-disabled': actionDropdownApiLoading,
                    }"
                  >
                    <i class="far fa-plus-square"></i>Neue Aktion hinzufügen
                  </b-dropdown-item>
                </template>
              </generalDropdown>
            </template>
          </ul>
        </div>

        <div
          class="edit-item-button"
          @click="onEditItemClick"
          v-show="!task?.loading"
        >
          Öffnen
        </div>

        <CustomLoader :size="40" v-show="task?.loading" />
      </div>

      <template v-for="(item, indx) in keysToRender">
        <div
          v-if="
            item.name in task &&
            getItemValue(task[item.name], item.name, item.type) &&
            item.type !== 'objectlist'
          "
          class="kanban-card-item"
          :class="{ first: indx == 0 }"
          :key="item.name"
        >
          {{ getItemValue(task[item.name], item.name, item.type) }}
        </div>
        <div
          v-else-if="
            item.type === 'objectlist' &&
            item.name in task &&
            hasObjectListValue(task[item.name])
          "
          class="kanban-card-item object-list-item"
          :class="{ first: indx == 0 }"
          :key="item.name"
        >
          <span class="object-list-preview">
            {{ getObjectListPreview(task[item.name], item) }}
          </span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import GeneralDropdown from '../core/general-dropdown.vue';
import CustomLoader from '../widgets/CustomLoader.vue';
import KanbanAvatarGroup from './KanbanAvatarGroup.vue';

export default {
  name: 'kanbanCard',

  props: {
    task: { type: Object, default: () => ({}) },
    taskIndex: { type: Number },
    dropTargetId: { type: String, required: true },
    keysToRender: { type: Array, default: () => [] },
    dragContext: { type: Object, required: true },
    database: { type: Object, required: true },
    actionFlowList: { type: Array, default: () => [] },
    isDatabaseDropDownLoading: { type: Function, default: () => {} },
    actionDropdownApiLoading: { type: Boolean, default: false },
    isItemSelected: { type: Function, required: true, default: () => {} },
    statusFieldKey: { type: String, default: 'status' },
    statusFieldRequired: { type: Boolean, default: false },
    columnKey: { type: String, default: '' },
    isNoStatusColumn: { type: Boolean, default: false },
  },

  data() {
    return {
      actions: [
        { name: 'share', label: 'Teilen', visible: true },
        { name: 'log', label: 'Protokoll', visible: true },
        {
          name: 'copy',
          label: 'Duplizieren',
          visible: this.database.access_level != 'read',
        },
        { name: 'download', label: 'herunterladen', visible: true },
        {
          name: 'delete',
          label: 'Löschen',
          visible:
            this.database.access_level !== 'read' && !this.database.add_only,
        },
        { name: 'more', label: 'Mehr', visible: true },
      ],
    };
  },

  methods: {
    setActiveDropTarget(dropPosition) {
      this.$set(this.dragContext, 'activeDropTargetId', this.dropTargetId);
      this.$set(this.dragContext, 'activeDropPosition', dropPosition);
      this.$set(this.dragContext, 'activeDropForbiddenId', null);
      this.$set(this.dragContext, 'activeDropForbiddenMessage', null);
    },

    setForbiddenDropTarget() {
      this.$set(
        this.dragContext,
        'activeDropForbiddenId',
        this.columnKey || this.dropTargetId
      );
      this.$set(
        this.dragContext,
        'activeDropForbiddenMessage',
        'Status ist ein Pflichtfeld'
      );
      this.$set(this.dragContext, 'activeDropTargetId', null);
      this.$set(this.dragContext, 'activeDropPosition', null);
    },

    clearActiveDropTarget() {
      this.$set(this.dragContext, 'activeDropTargetId', null);
      this.$set(this.dragContext, 'activeDropPosition', null);
      this.$set(this.dragContext, 'activeDropForbiddenId', null);
      this.$set(this.dragContext, 'activeDropForbiddenMessage', null);
    },

    onDragStart(event) {
      this.$emit('onDragStart', this.task.id);
      this.clearActiveDropTarget();

      const cloneContainer = document.getElementById(
        'dragged-task-clone-container'
      );

      const draggedElement = event?.srcElement?.parentElement;

      const draggedImage = event?.srcElement
        ?.querySelector('.kanban-card')
        ?.cloneNode(true);

      if (draggedImage && cloneContainer) {
        draggedImage.style.setProperty(
          'width',
          draggedElement.offsetWidth + 'px',
          'important'
        );

        draggedImage.style.setProperty('background', '#0000004f');
        cloneContainer.appendChild(draggedImage);

        event.dataTransfer.setDragImage(
          draggedImage,
          event.offsetX,
          event.offsetY
        );
      }
    },

    onDragOver(event) {
      if (!this.dragContext.isDragging) return;

      if (this.isDraggingSelf) {
        this.clearActiveDropTarget();
        return;
      }

      if (this.isForbiddenColumn) {
        this.setForbiddenDropTarget();
        return;
      }

      const bounds = event.currentTarget.getBoundingClientRect();
      const middleY = bounds.top + bounds.height / 2;
      const dropPosition = event.clientY < middleY ? 'before' : 'after';

      this.setActiveDropTarget(dropPosition);
    },

    onDrop() {
      if (this.isDraggingSelf) {
        this.clearActiveDropTarget();
        return;
      }
      if (this.isForbiddenColumn) {
        this.clearActiveDropTarget();
        this.$emit('onForbiddenDrop', 'Status ist ein Pflichtfeld');
        return;
      }

      const dropPosition = this.activeDropPosition;

      setTimeout(() => {
        this.$emit('onDrop', {
          dropTaskIndex: this.taskIndex,
          dropPosition,
          task: this.task,
        });

        this.clearActiveDropTarget();
        this.removeDragImageElement();
      }, 50);
    },

    onDragEnd() {
      this.dragContext.isDragging = false;

      this.clearActiveDropTarget();
      this.removeDragImageElement();

      this.$emit('onDragEnd');
    },

    getItemValue(value, keyName, itemType = '') {
      if (keyName === this.statusFieldKey && !value) return 'Kein Status';
      if (value === 'nostatus') return 'Kein Status';
      if (!Array.isArray(value) && typeof value === 'object') return '';

      if (Array.isArray(value)) {
        if (itemType === 'objectlist') {
          return '';
        }
        return value.join(',');
      }

      return value;
    },
    hasObjectListValue(value) {
      return (
        Array.isArray(value) &&
        value.length > 0 &&
        value.some((entry) => entry && typeof entry === 'object')
      );
    },
    getObjectListEntries(entry, item) {
      const objectEntry = entry && typeof entry === 'object' ? entry : {};
      const keys = Object.keys(objectEntry);
      const schemaNames = (item?.schema || [])
        .map((field) => field?.name)
        .filter((name) => typeof name === 'string' && name.length > 0);
      const keyOrderMap = new Map(
        schemaNames.map((name, index) => [name, index])
      );

      const orderedKeys = [...keys].sort((a, b) => {
        const aOrder = keyOrderMap.has(a)
          ? keyOrderMap.get(a)
          : Number.MAX_SAFE_INTEGER;
        const bOrder = keyOrderMap.has(b)
          ? keyOrderMap.get(b)
          : Number.MAX_SAFE_INTEGER;
        if (aOrder === bOrder) return a.localeCompare(b);
        return aOrder - bOrder;
      });

      return orderedKeys.map((key) => [key, objectEntry[key]]);
    },
    getObjectListFieldLabel(item, fieldKey) {
      const schemaField = (item?.schema || []).find((f) => f?.name === fieldKey);
      return schemaField?.label || fieldKey;
    },
    formatObjectListFieldValue(value) {
      if (value === null || value === undefined) return '';
      if (Array.isArray(value)) return value.join(', ');
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    },
    getObjectListPreview(value, item) {
      if (!this.hasObjectListValue(value)) return '';
      const segments = [];
      value.forEach((entry) => {
        this.getObjectListEntries(entry, item).forEach(([fieldKey, fieldValue]) => {
          const label = this.getObjectListFieldLabel(item, fieldKey);
          const text = this.formatObjectListFieldValue(fieldValue);
          if (text.trim()) {
            segments.push(`${label}: ${text}`);
          }
        });
      });
      return segments.join(', ');
    },

    onEditItemClick() {
      this.$router.push({
        path: '/',
        query: {
          section: 'data',
          database: this.database.name,
          item: this.task.id,
        },
      });
    },

    removeDragImageElement() {
      const cloneContainer = document.getElementById(
        'dragged-task-clone-container'
      );

      if (cloneContainer) {
        cloneContainer.innerHTML = '';
      }
    },
  },

  computed: {
    ...mapGetters({
      userHasPermissionToAddFlow: 'getUserPermissionToAddFlow',
    }),

    isActiveDropTarget() {
      return this.dragContext?.activeDropTargetId === this.dropTargetId;
    },

    activeDropPosition() {
      if (!this.isActiveDropTarget) return null;

      return this.dragContext?.activeDropPosition || null;
    },

    isDraggingSelf() {
      if (!this.dragContext?.isDragging) return false;

      return String(this.dragContext?.currentCardId) === String(this.task?.id);
    },

    isForbiddenColumn() {
      return this.isNoStatusColumn && this.statusFieldRequired;
    },
  },

  components: {
    CustomLoader,
    GeneralDropdown,
    KanbanAvatarGroup,
  },
};
</script>

<style scoped>
.kanban-card-wrapper {
  position: relative;
  padding: 6px 0px;
}

.kanban-card-wrapper::before {
  content: '';
  width: 100%;
  height: 4px;
  position: absolute;
  left: 0;
  background: #009cff;
  display: none;
  pointer-events: none;
}

.kanban-card-wrapper.visible.before::before {
  top: -2px;
  display: block;
}

.kanban-card-wrapper.visible.after::before {
  bottom: -2px;
  display: block;
}

.kanban-card:hover {
  box-shadow: 0px 3px 15px 2px #0000001a;
}

.kanban-card {
  padding: 22px 29px;
  box-shadow: 0px 0px 5px 0px #00000026;
  border: none;
  border-radius: 10px;
  position: relative;
  z-index: 10;
  min-height: 84px;
}

.kanban-card.selected {
  background-color: rgba(220, 220, 220, 0.16);
  outline: 1px solid #0fbf00;
  outline-offset: -1px;
  box-shadow: none;
}

.kanban-card::before {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  z-index: 20;
  pointer-events: none;
}

.kanban-card .kanban-card-overlay {
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  z-index: 30;
  background-color: #3b3d49d1;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  opacity: 0;
  transition: opacity 0.45s ease-in-out;
}

.kanban-card:hover .kanban-card-overlay {
  opacity: 1;
}
.kanban-card:hover {
  z-index: 11;
}

.kanban-card .kanban-card-overlay.is-loading {
  opacity: 1;
}

.kanban-card .kanban-card-overlay.checked {
  opacity: 1;
  background-color: rgba(59, 61, 73, 0.14);
}

.kanban-card .kanban-card-overlay.checked .checkbox-container {
  opacity: 1;
}

.kanban-card:hover .kanban-card-overlay.checked {
  background-color: #3b3d49d1;
}

.kanban-card .kanban-card-item.first {
  color: #636381;
  font-size: 16px;
  font-weight: 400;
  line-height: 27px;
  font-family: 'AktivGroteskMedium';
}

.kanban-card .kanban-card-item {
  color: #636381;
  font-size: 14px;
  font-weight: 400;
  line-height: 36px;
  height: 27px;
}

.kanban-card .kanban-card-item.object-list-item {
  position: relative;
  overflow: hidden;
}

.kanban-card .kanban-card-item .object-list-preview {
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: inherit;
}

.kanban-card .kanban-card-overlay .tools {
  position: absolute;
  right: 17px;
  top: 15px;
  border: 0px solid transparent;
  -webkit-transition: visibility 0s ease 450ms, -webkit-box-shadow 0.2s ease;
  -o-transition: visibility 0s ease 450ms, box-shadow 0.2s ease;
  transition: visibility 0s ease 450ms, box-shadow 0.2s ease;
  transition: visibility 0s ease 450ms, box-shadow 0.2s ease,
    -webkit-box-shadow 0.2s ease;
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 0px;
  padding: 0px;
  opacity: 0;
  pointer-events: none;
  transform: translateY(-2px);
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
}

.kanban-card:hover .kanban-card-overlay .tools {
  opacity: 1;
  pointer-events: auto;
  transform: translateY(0);
}

.kanban-card .kanban-card-overlay .tools li {
  list-style: none;
  cursor: pointer;
}

.kanban-card .kanban-card-overlay .tools a {
  text-indent: -9999px;
}

.kanban-card .kanban-card-overlay .tools li {
  opacity: 0.7;
  background-image: url('../../assets/img/project-sprite.png');
  background-size: 882px 840px;
  background-position: -868px -268px;
  width: 16px;
  height: 16px;
  background-repeat: no-repeat;
  display: block;
  transition: opacity 0.2s ease-in-out;
  -webkit-transition: opacity 0.2s ease-in-out;
  -o-transition: opacity 0.2s ease-in-out;
}

.kanban-card
  .kanban-card-overlay
  .tools
  ::v-deep
  .dropdown-toggle.btn:focus-visible {
  box-shadow: none;
  -webkit-box-shadow: none !important;
}
.kanban-card .kanban-card-overlay ::v-deep .dropdown-toggle li {
  height: 12px;
}

.kanban-card .kanban-card-overlay .tools ::v-deep .dropdown-menu {
  background: #fff;
  width: auto;
  min-width: 130px;
  padding: 0;
  box-shadow: 0 2px 35px rgba(31, 37, 50, 0.14),
    0 0 0 1px rgba(76, 86, 103, 0.2);
  border: 0.1px solid #f0f0f0;
  max-height: 300px;
  z-index: 9;
  -ms-scroll-chaining: none;
  overscroll-behavior: contain;
}

.kanban-card .kanban-card-overlay .tools ::v-deep .dropdown-menu ul {
  height: auto;
}

.kanban-card
  .kanban-card-overlay
  .tools
  ::v-deep
  .dropdown
  .dropdown-menu
  .dropdown-action-item
  .dropdown-item {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

.kanban-card
  .kanban-card-overlay
  .tools
  ::v-deep
  .dropdown
  .dropdown-menu
  .dropdown-action-item {
  height: auto;
  width: 100%;
  position: relative;
  opacity: 1;
}

.kanban-card
  .kanban-card-overlay
  .tools
  ::v-deep
  .dropdown
  .dropdown-menu
  .dropdown-action-item.pointer-disabled {
  pointer-events: none;
}

.kanban-card
  .kanban-card-overlay
  .tools
  ::v-deep
  .dropdown
  .dropdown-menu
  .dropdown-action-item
  .workflow-icon {
  vertical-align: top;
  border-style: none;
  width: 25px;
  margin-right: 3px;
  padding-left: 2px;
  height: 25px;
}

.kanban-card
  .kanban-card-overlay
  .tools
  ::v-deep
  .dropdown
  .dropdown-menu
  .dropdown-action-item
  .workflow-action {
  text-overflow: ellipsis;
  overflow-x: hidden;
  width: 117px;
  max-width: 100%;
  white-space: nowrap;
  color: black;
}

.kanban-card .kanban-card-overlay .tools li:hover {
  opacity: 1;
}

.kanban-card .kanban-card-overlay .tools li.share {
  background-position: -868px -266px;
}

.kanban-card .kanban-card-overlay .tools li.log {
  background-position: -800px -470px;
  filter: brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(2%)
    hue-rotate(360deg) brightness(105%) contrast(101%);
}

.kanban-card .kanban-card-overlay .tools li.download {
  background-position: -868px -644px;
}

.kanban-card .kanban-card-overlay .tools li.delete {
  background-position: -129px -757px;
}

.kanban-card .kanban-card-overlay .tools li.copy {
  background-position: -867px -455px;
}

.kanban-card .kanban-card-overlay .tools li.more {
  background-position: -323px -754px;
  width: 22px;
}

.kanban-card .kanban-card-overlay .checkbox-container {
  height: 20px;
  width: 20px;
  margin: 15px 0 0 16px;
  -webkit-transition: opacity 0.2s ease;
  -o-transition: opacity 0.2s ease;
  transition: opacity 0.2s ease;
  opacity: 0.7;
  display: block;
  line-height: 21px;
  position: absolute;
  top: 0;
  left: 0;
}

.kanban-card .kanban-card-overlay .checkbox-container input,
.kanban-card .kanban-card-overlay .checkbox-container input[type='checkbox'] {
  cursor: pointer;
  visibility: visible;
  height: 21px;
  width: 20px;
  display: inline-block;
  background: 0 0;
  opacity: 0;
  margin-bottom: 2%;
}

.kanban-card .kanban-card-overlay .checkbox-container span {
  background-image: url('../../assets/img/project-sprite.png');
  background-size: 882px 840px;
  background-position: -488px -386px;
  height: 20px;
  width: 20px;
  background-repeat: no-repeat;
  overflow: hidden;
  display: block;
  text-align: center;
}

.kanban-card .kanban-card-overlay .checkbox-container span.checked {
  background-position: -398px -283px;
}

input[type='checkbox'],
input[type='radio'] {
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  padding: 0;
  -webkit-font-smoothing: antialiased;
  outline: 0;
  margin: 0 0 9px;
  font-size: 100%;
  vertical-align: middle;
  display: inline-block;
}

.kanban-card .kanban-card-overlay .edit-item-button {
  width: 125px;
  height: 36px;
  border: 1px solid #ffffff40;
  border-radius: 2px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  opacity: 0;
  transition: opacity 0.2s ease-in-out, border-color 0.2s ease-in-out;
}
.kanban-card:not(:has(.kanban-card-item:nth-of-type(3))) .edit-item-button {
  top: 60%;
}
.kanban-card:hover .kanban-card-overlay .edit-item-button {
  opacity: 1;
}

.kanban-card .kanban-card-overlay .edit-item-button:hover {
  border: 1px solid #fff;
}
</style>
